/**
 * @fileoverview useChat カスタムフック
 * @description チャット機能の状態管理とAPI通信を行うフック
 */
import { useState, useCallback } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import type {
  ChatRequest,
  ChatResponse,
  PromptTemplate,
  ChatSource,
  CharacterType as SharedCharacterType,
} from '@karosorabei/shared'

import { chatApi } from '@/lib/api'
import type { CharacterType } from '@/components/chat/character-avatar'

/**
 * チャットメッセージの型
 */
export interface ChatMessage {
  /** メッセージID */
  id: string
  /** 送信者の役割 */
  role: 'user' | 'assistant'
  /** メッセージ内容 */
  content: string
  /** AIキャラクター（assistantの場合） */
  character?: CharacterType
  /** ソース情報（assistantの場合） */
  sources?: ChatSource[]
  /** クエリ拡張で生成されたキーワード（ハイライト用） */
  expandedKeywords?: string[]
  /** 作成日時 */
  createdAt: Date
}

/**
 * useChatフックの戻り値型
 */
export interface UseChatReturn {
  /** メッセージ一覧 */
  messages: ChatMessage[]
  /** メッセージ送信中かどうか */
  isLoading: boolean
  /** 送信中フラグ（isLoadingのエイリアス） */
  isSending: boolean
  /** エラー情報 */
  error: string | null
  /** FAQテンプレート一覧 */
  templates: PromptTemplate[]
  /** テンプレート読み込み中かどうか */
  isLoadingTemplates: boolean
  /** 残りメッセージ回数（未ログイン時） */
  remainingMessages: number | undefined
  /** メッセージ送信 */
  sendMessage: (message: string) => void
  /** テンプレートからメッセージ送信 */
  sendFromTemplate: (template: PromptTemplate) => void
  /** テンプレートを使用（sendFromTemplateのエイリアス） */
  useTemplate: (template: PromptTemplate) => void
  /** メッセージをクリア */
  clearMessages: () => void
  /** エラーをクリア */
  clearError: () => void
}

/**
 * ユニークIDを生成
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

/**
 * 共有パッケージのCharacterTypeをフロントエンドのCharacterTypeに変換
 */
function convertCharacter(character: SharedCharacterType): CharacterType {
  return character as CharacterType
}

/**
 * useChatフック
 * @description チャット機能を提供するカスタムフック
 * @example
 * const { messages, isLoading, sendMessage, templates } = useChat()
 */
export function useChat(): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // TODO: 実際のAPI応答から取得する
  const [remainingMessages] = useState<number | undefined>(undefined)

  /**
   * FAQテンプレート取得
   */
  const { data: templates = [], isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['chat', 'templates'],
    queryFn: chatApi.getTemplates,
    staleTime: 1000 * 60 * 30, // 30分キャッシュ
  })

  /**
   * メッセージ送信ミューテーション
   */
  const sendMutation = useMutation({
    mutationFn: async (request: ChatRequest): Promise<ChatResponse> => {
      return chatApi.send(request)
    },
    onSuccess: (response) => {
      // エラーをクリア
      setErrorMessage(null)

      // セッションIDを保存
      if (!sessionId && response.sessionId) {
        setSessionId(response.sessionId)
      }

      // 対話形式の回答を複数のメッセージに分割して追加
      const aiMessages: ChatMessage[] = response.dialogue.map((line, index) => {
        const isLastMessage = index === response.dialogue.length - 1
        const baseMessage = {
          id: generateId(),
          role: 'assistant' as const,
          content: line.text,
          character: convertCharacter(line.character),
          createdAt: new Date(),
        }
        // 最後のメッセージにのみソースとexpandedKeywordsを添付
        if (isLastMessage && response.sources.length > 0) {
          return {
            ...baseMessage,
            sources: response.sources,
            // expandedKeywordsはオプショナルなので、undefinedの場合は空配列にする
            expandedKeywords: response.expandedKeywords ?? [],
          }
        }
        return baseMessage
      })

      setMessages((prev) => [...prev, ...aiMessages])
    },
    onError: (error) => {
      // エラーメッセージを設定
      setErrorMessage(error instanceof Error ? error.message : 'エラーが発生しました')
    },
  })

  /**
   * メッセージ送信
   */
  const sendMessage = useCallback(
    (message: string) => {
      // ユーザーメッセージを追加
      const userMessage: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: message,
        createdAt: new Date(),
      }

      setMessages((prev) => [...prev, userMessage])

      // APIリクエスト送信
      sendMutation.mutate({
        message,
        sessionId,
      })
    },
    [sessionId, sendMutation]
  )

  /**
   * テンプレートからメッセージ送信
   * @description カードに表示されているpromptをそのままユーザーメッセージとして表示
   */
  const sendFromTemplate = useCallback(
    (template: PromptTemplate) => {
      // ユーザーメッセージを追加（カードに表示されているpromptをそのまま使用）
      const userMessage: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: template.prompt,
        createdAt: new Date(),
      }

      setMessages((prev) => [...prev, userMessage])

      // APIリクエスト送信（テンプレートIDを含む）
      sendMutation.mutate({
        message: template.prompt,
        sessionId,
        templateId: template.id,
      })
    },
    [sessionId, sendMutation]
  )

  /**
   * メッセージをクリア
   */
  const clearMessages = useCallback(() => {
    setMessages([])
    setSessionId(undefined)
    setErrorMessage(null)
  }, [])

  /**
   * エラーをクリア
   */
  const clearError = useCallback(() => {
    setErrorMessage(null)
  }, [])

  return {
    messages,
    isLoading: sendMutation.isPending,
    isSending: sendMutation.isPending,
    error: errorMessage,
    templates,
    isLoadingTemplates,
    remainingMessages,
    sendMessage,
    sendFromTemplate,
    useTemplate: sendFromTemplate,
    clearMessages,
    clearError,
  }
}
